import React, { forwardRef } from "react";
import PropTypes from "kf-proptypes/widgets";

import { parseNumber, formatNumber, getSeparators } from "./number.util.js";
import { FieldErrors, FieldHint, FieldLabel } from "../index.jsx";
import { NUMBER_TYPES } from "./constants.js";
import { EditableNumber } from "./editable.jsx";
import { ReadonlyNumber } from "./readonly.jsx";

import styles from "./number.css";

// eslint-disable-next-line kf/const-naming-convention

export const Number = forwardRef(
  (
    {
      name,
      value,
      readOnly,
      readonly,
      disabled,
      className,
      currencySymbol,
      wrapperClassName,
      placeholder,
      autoFocus,
      autoComplete,
      onBlur,
      onChange,
      onFocus,
      decimalPoint,
      onKeyDown,
      selectedCurrency,
      styleType,
      readonlyWrap,
      autoGrow,
      label,
      hint,
      errors,
      required,
      size,
      labelLeftIcon,
      labelRightIcon,
      id,
      isError,
      addonType,
      ...restProps
    },
    ref
  ) => {
    return (
      <div
        className={`${className} ${styles.numberWrapper}`}
        style={{ width: autoGrow ? `${(value ?? "").length}ch` : "" }}
        data-component="number"
      >
        {label && (
          <FieldLabel
            disabled={disabled}
            label={label}
            targetId={id}
            required={required}
            leftIcon={labelLeftIcon}
            rightIcon={labelRightIcon}
            size={size}
          />
        )}
        {readOnly || readonly ? (
          <ReadonlyNumber
            value={value}
            className={className}
            size={size}
            id={id}
            decimalPoint={decimalPoint}
            currencySymbol={currencySymbol}
            styleType={styleType}
            isError={isError}
            errors={errors}
            addonType={addonType}
            disabled={disabled}
            readonlyWrap={readonlyWrap}
            wrapperClassName={wrapperClassName}
          />
        ) : (
          <EditableNumber
            name={name}
            styleType={styleType}
            autoFocus={autoFocus}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            value={value}
            onKeyDown={onKeyDown}
            placeholder={currencySymbol || placeholder}
            readonly={readOnly}
            disabled={disabled}
            className={styles.number}
            autoComplete={autoComplete}
            decimalPoint={decimalPoint}
            readonlyWrap={readonlyWrap}
            innerRef={ref}
            size={size}
            id={id}
            currencySymbol={currencySymbol}
            isError={isError}
            errors={errors}
            addonType={addonType}
            wrapperClassName={wrapperClassName}
            {...restProps}
          />
        )}
        {hint && <FieldHint hint={hint} targetId={id} />}
        {errors && errors.length > 0 && (
          <FieldErrors errors={errors} targetId={id} />
        )}
      </div>
    );
  }
);

Number.propTypes = {
  /** value which user enter */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Placeholder for input field */
  placeholder: PropTypes.string,
  /** Custom class name from consumer side */
  className: PropTypes.string,
  /** Required is mandatory field */
  required: PropTypes.bool,
  /** unique id for input field */
  id: PropTypes.string,
  /** label for input field */
  label: PropTypes.string,
  /** hint for input field */
  hint: PropTypes.string,
  /** errors which will show error */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** onClick enables click for user */
  onClick: PropTypes.func,
  autoComplete: PropTypes.bool,
  /** name is unique props for input field */
  name: PropTypes.string,
  /** readonly prop mentions whether input need to readonly or not */
  readOnly: PropTypes.bool,
  /** readonly prop mentions whether input need to readonly or not, many places using */
  readonly: PropTypes.bool,
  /** disabled prop indicates whether input filed is disabled or not */
  disabled: PropTypes.bool,
  /** indicates blur on input filed */
  onBlur: PropTypes.func,
  /** onchange will trigger when user changes any data in input field */
  onChange: PropTypes.func,
  /** onfocus will trigger when user clicks on input filed */
  onFocus: PropTypes.func,
  /** when user click and enters any key in keyboard */
  onKeyDown: PropTypes.func,
  /** when user click and enters any key in keyboard */
  onKeyUp: PropTypes.func,
  /** onEnter will trigger when user clicks enter in keyboard */
  onEnter: PropTypes.func,
  /** indicated type of input filed whether text or password */
  type: PropTypes.string,
  /** it will render left icon of input field */
  leftIcon: PropTypes.node,
  /** it will render right icon of input field */
  rightIcon: PropTypes.node,
  /** autofocus */
  autoFocus: PropTypes.bool,
  /** styletype is whether input is normal or minimal indication */
  styleType: PropTypes.string,
  /** size indicates for input filed whether it will be medium etc */
  size: PropTypes.string,
  /** className */
  wrapperClassName: PropTypes.string,
  /** bool to indicate error or not */
  isError: PropTypes.bool,
  /** handleReadonlyLink */
  handleReadonlyLink: PropTypes.bool,
  readonlyWrap: PropTypes.bool,
  /** iconRender funcion for input field */
  iconRender: PropTypes.func,
  /** addonType funcion for input field */
  addonType: PropTypes.string,
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node,
  /**
   * used to mention currency formate
   */
  currencySymbol: PropTypes.string,
  /**
   * 	selected currency formate
   */
  selectedCurrency: PropTypes.string,
  /**
   * specify the number of decimal places that input uses to display numbers
   */
  decimalPoint: PropTypes.number,
  autoGrow: PropTypes.bool
};

Number.defaultProps = {
  onBlur: () => {},
  onFocus: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  styleType: NUMBER_TYPES.NORMAL,
  errors: [],
  autoGrow: false,
  readOnly: false,
  disabled: false
};

export { parseNumber, formatNumber, getSeparators, NUMBER_TYPES as NumberType };
